// Service Worker Registration Utility
// Handles registration, updates, and lifecycle management

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
    window.location.hostname === '[::1]' ||
    window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/)
);

type Config = {
  onSuccess?: (registration: ServiceWorkerRegistration) => void;
  onUpdate?: (registration: ServiceWorkerRegistration) => void;
  onOfflineReady?: () => void;
};

export function register(config?: Config) {
  if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
    const publicUrl = new URL(process.env.PUBLIC_URL, window.location.href);
    if (publicUrl.origin !== window.location.origin) {
      return;
    }

    window.addEventListener('load', () => {
      const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`;

      if (isLocalhost) {
        checkValidServiceWorker(swUrl, config);
        navigator.serviceWorker.ready.then(() => {
        });
      } else {
        registerValidSW(swUrl, config);
      }
    });
  }
}

function registerValidSW(swUrl: string, config?: Config) {
  navigator.serviceWorker
    .register(swUrl)
    .then((registration) => {

      // Check for updates periodically
      setInterval(() => {
        registration.update();
      }, 60 * 60 * 1000); // Check every hour

      registration.onupdatefound = () => {
        const installingWorker = registration.installing;
        if (installingWorker == null) {
          return;
        }

        installingWorker.onstatechange = () => {
          if (installingWorker.state === 'installed') {
            if (navigator.serviceWorker.controller) {

              if (config && config.onUpdate) {
                config.onUpdate(registration);
              }

              // Show update notification
              showUpdateNotification(registration);
            } else {

              if (config && config.onSuccess) {
                config.onSuccess(registration);
              }

              if (config && config.onOfflineReady) {
                config.onOfflineReady();
              }
            }
          }
        };
      };
    })
    .catch((error) => {
      console.error('Error during service worker registration:', error);
    });
}

function checkValidServiceWorker(swUrl: string, config?: Config) {
  fetch(swUrl, {
    headers: { 'Service-Worker': 'script' },
  })
    .then((response) => {
      const contentType = response.headers.get('content-type');
      if (
        response.status === 404 ||
        (contentType != null && contentType.indexOf('javascript') === -1)
      ) {
        navigator.serviceWorker.ready.then((registration) => {
          registration.unregister().then(() => {
            window.location.reload();
          });
        });
      } else {
        registerValidSW(swUrl, config);
      }
    })
    .catch(() => {
    });
}

function showUpdateNotification(registration: ServiceWorkerRegistration) {
  // Create a notification to inform user about update
  const updateDiv = document.createElement('div');
  updateDiv.id = 'sw-update-notification';
  updateDiv.style.cssText = `
    position: fixed;
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%);
    background: #0ea5e9;
    color: white;
    padding: 16px 24px;
    border-radius: 8px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    z-index: 10000;
    display: flex;
    align-items: center;
    gap: 16px;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
    font-size: 14px;
    max-width: 90%;
    animation: slideUp 0.3s ease-out;
  `;

  updateDiv.innerHTML = `
    <span>🚀 A new version is available!</span>
    <button id="sw-update-btn" style="
      background: white;
      color: #0ea5e9;
      border: none;
      padding: 8px 16px;
      border-radius: 4px;
      cursor: pointer;
      font-weight: 600;
      font-size: 14px;
    ">
      Update
    </button>
    <button id="sw-dismiss-btn" style="
      background: transparent;
      color: white;
      border: 1px solid white;
      padding: 8px 16px;
      border-radius: 4px;
      cursor: pointer;
      font-size: 14px;
    ">
      Later
    </button>
  `;

  // Add animation keyframe
  const style = document.createElement('style');
  style.textContent = `
    @keyframes slideUp {
      from {
        transform: translateX(-50%) translateY(100px);
        opacity: 0;
      }
      to {
        transform: translateX(-50%) translateY(0);
        opacity: 1;
      }
    }
  `;
  document.head.appendChild(style);

  document.body.appendChild(updateDiv);

  // Update button handler
  const updateBtn = document.getElementById('sw-update-btn');
  if (updateBtn) {
    updateBtn.onclick = () => {
      if (registration.waiting) {
        registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        window.location.reload();
      }
    };
  }

  // Dismiss button handler
  const dismissBtn = document.getElementById('sw-dismiss-btn');
  if (dismissBtn) {
    dismissBtn.onclick = () => {
      updateDiv.remove();
    };
  }

  // Auto-dismiss after 30 seconds
  setTimeout(() => {
    if (document.getElementById('sw-update-notification')) {
      updateDiv.remove();
    }
  }, 30000);
}

export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister();
      })
      .catch((error) => {
        console.error(error.message);
      });
  }
}

// Helper function to check if app is installable
export function checkInstallability() {
  let deferredPrompt: any;

  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredPrompt = e;
    
    
    // Show custom install prompt
    showInstallPrompt(deferredPrompt);
  });

  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
  });
}

function showInstallPrompt(deferredPrompt: any) {
  // Create install prompt
  const installDiv = document.createElement('div');
  installDiv.id = 'pwa-install-prompt';
  installDiv.style.cssText = `
    position: fixed;
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%);
    background: white;
    color: #1a202c;
    padding: 16px 24px;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 10000;
    display: flex;
    align-items: center;
    gap: 16px;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
    font-size: 14px;
    max-width: 90%;
    border: 1px solid #e2e8f0;
  `;

  installDiv.innerHTML = `
    <span>📱 Install Dawr app for a better experience!</span>
    <button id="pwa-install-btn" style="
      background: #0ea5e9;
      color: white;
      border: none;
      padding: 8px 16px;
      border-radius: 4px;
      cursor: pointer;
      font-weight: 600;
      font-size: 14px;
    ">
      Install
    </button>
    <button id="pwa-dismiss-btn" style="
      background: transparent;
      color: #64748b;
      border: none;
      padding: 8px;
      cursor: pointer;
      font-size: 18px;
      line-height: 1;
    ">
      ×
    </button>
  `;

  document.body.appendChild(installDiv);

  // Install button handler
  const installBtn = document.getElementById('pwa-install-btn');
  if (installBtn) {
    installBtn.onclick = async () => {
      deferredPrompt.prompt();
      await deferredPrompt.userChoice;
      installDiv.remove();
    };
  }

  // Dismiss button handler
  const dismissBtn = document.getElementById('pwa-dismiss-btn');
  if (dismissBtn) {
    dismissBtn.onclick = () => {
      installDiv.remove();
      // Store dismissal in localStorage to not show again for a while
      localStorage.setItem('pwa-install-dismissed', Date.now().toString());
    };
  }

  // Don't show if user dismissed recently (within 7 days)
  const dismissedTime = localStorage.getItem('pwa-install-dismissed');
  if (dismissedTime) {
    const daysSinceDismissed = (Date.now() - parseInt(dismissedTime)) / (1000 * 60 * 60 * 24);
    if (daysSinceDismissed < 7) {
      installDiv.remove();
      return;
    }
  }
}

// Check if running as PWA
export function isPWA(): boolean {
  return window.matchMedia('(display-mode: standalone)').matches ||
         (window.navigator as any).standalone === true ||
         document.referrer.includes('android-app://');
}

// Get PWA display mode
export function getPWADisplayMode(): string {
  if (isPWA()) {
    return 'standalone';
  }
  if (window.matchMedia('(display-mode: fullscreen)').matches) {
    return 'fullscreen';
  }
  if (window.matchMedia('(display-mode: minimal-ui)').matches) {
    return 'minimal-ui';
  }
  return 'browser';
}

// Track display mode changes
export function trackDisplayMode() {
  window.matchMedia('(display-mode: standalone)').addEventListener('change', (_e) => {
    // Track display mode changes (e.g., when app is installed as PWA)
  });
}
